<template>
  <div class="poll-progress" :class="{ 'poll-progress--compact': compact }">
    <!-- compact: one status line above the result list -->
    <template v-if="compact">
      <span :style="{ color: daysDone ? okColor : '' }">
        <v-icon small :color="daysDone ? okColor : ''" aria-hidden="true">{{
          daysDone ? "mdi-check-circle" : "mdi-circle-outline"
        }}</v-icon>
        {{ daysLabel }}: {{ daysDistributed }} von {{ daysRequired }}
      </span>
      <span v-if="requireStars" :style="{ color: starsDone ? okColor : '' }">
        <v-icon small :color="starsDone ? okColor : ''" aria-hidden="true">{{
          starsDone ? "mdi-check-circle" : "mdi-circle-outline"
        }}</v-icon>
        Bewertet: {{ nRated }} von {{ nToRate }}
        {{ starsRequiredFor === "all" ? "Varianten" : "genutzten Varianten" }}
      </span>
    </template>
    <!-- full: checklist of what is still missing before the poll can be sent -->
    <template v-else>
      <div v-if="missing.length > 0" role="status">
        <div class="poll-progress__headline">
          Bevor es weitergeht, fehlt noch:
        </div>
        <ul class="poll-progress__list">
          <li v-for="key in missing" :key="key">{{ messages[key] }}</li>
        </ul>
      </div>
      <div v-else class="poll-progress__complete" :style="{ color: okColor }">
        <v-icon :color="okColor" aria-hidden="true">mdi-check-circle</v-icon>
        Alle Angaben sind vollständig.
      </div>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import { storeToRefs } from "pinia";
import { usePollStore } from "../../store/poll-store";

/**
 * Shows the progress of the poll input: how many days are distributed over the
 * transport variants, how many used variants are rated and - in the full variant -
 * a checklist of everything that is still missing before "Weiter" is enabled.
 */
export default Vue.extend({
  name: "PollProgress",

  props: {
    // compact = single status line, otherwise the full checklist
    compact: { type: Boolean, default: false },
  },

  computed: {
    store() {
      return usePollStore();
    },
    okColor() {
      return this.$vuetify.theme.themes.light.secondary;
    },
    frequencyType() {
      return storeToRefs(this.store).frequencyType.value;
    },
    daysLabel() {
      return this.frequencyType === "percentage"
        ? "Verteilte Prozent"
        : "Verteilte Arbeitstage";
    },
    daysDistributed() {
      return this.store.daysDistributed;
    },
    daysRequired() {
      return this.store.daysRequired;
    },
    daysDone() {
      return this.store.isPollFrequenciesValid();
    },
    starsRequiredFor() {
      return this.store.starsRequiredFor;
    },
    requireStars() {
      return !!this.starsRequiredFor;
    },
    nToRate() {
      return this.store.transportIdsRequiringStars.length;
    },
    nRated() {
      const rated = this.store.ratedTransportIds;
      return this.store.transportIdsRequiringStars.filter((id) =>
        rated.includes(id)
      ).length;
    },
    starsDone() {
      return this.store.isPollStarsValid();
    },
    missing() {
      return this.store.missingPollItems();
    },
    messages() {
      const daysMsg =
        this.frequencyType === "percentage"
          ? `Die Anteile der Varianten müssen zusammen 100 % ergeben (aktuell ${this.daysDistributed} %).`
          : `Die Tage pro Woche müssen zusammen ${this.daysRequired} ergeben (aktuell ${this.daysDistributed}). Bitte die Werte bei den Varianten in Schritt 3 anpassen.`;
      return {
        situation: "Die Angaben in Schritt 1 sind ungültig.",
        days: daysMsg,
        stars:
          this.starsRequiredFor === "all"
            ? `Bitte bewerten Sie jede Variante mit Sternen (${this.nRated} von ${this.nToRate} bewertet).`
            : `Bitte bewerten Sie jede genutzte Variante mit Sternen (${this.nRated} von ${this.nToRate} bewertet).`,
        transportUsed: "Bitte wählen Sie genau eine Variante aus der Liste aus.",
        plz: "Bitte geben Sie die 5-stellige Postleitzahl Ihres Wohnorts an.",
      };
    },
  },
});
</script>

<style scoped>
.poll-progress {
  width: 100%;
}
.poll-progress--compact {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 24px;
  font-weight: 500;
  padding: 8px 12px;
  background-color: white;
  border-radius: 8px;
}
.poll-progress__headline {
  font-weight: bold;
}
.poll-progress__list {
  padding-left: 22px;
  margin-top: 4px;
  color: #b00020;
}
.poll-progress__complete {
  font-weight: 500;
}
</style>
